import { Link } from "react-router-dom";
import { ArrowRight, FileText, MapPin, Video } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { PageHeroCard } from "@/components/site/PageHeroCard";
import { SamplingLocationsSection } from "@/components/site/SamplingLocationsSection";
import { Button } from "@/components/ui/button";
import { useLang } from "@/i18n/LangContext";
import { PageMeta } from "@/components/site/PageMeta";

const Provtagning = () => {
  const { lang } = useLang();
  const sv = lang === "sv";

  const copy = sv
    ? {
        metaTitle: "Provtagning nära dig | Intygsläkaren",
        metaDescription:
          "Vi skickar remissen. Du lämnar prover på ett erbjudet provtagningsställe via Provio, Synlab eller Unilabs. Planering och uppföljning sköter vi online.",
        eyebrow: "Provtagning · remisser och hjälp online",
        title: "Vi skickar remissen. Du tar proverna nära dig.",
        lead: "Du behöver inte resa till Göteborg för prover som kan tas på ett av de erbjudna provtagningsställena. Remiss, planering, provsvar och uppföljning sköter vi online.",
        note: "På provtagningsstället lämnar du dina prover. Du behöver inte boka ett separat läkarbesök för att ordna själva provtagningen. Om ditt intyg kräver en läkarbedömning framgår det på sidan för det intyget.",
        primaryCta: "Hitta ditt läkarintyg",
        secondaryCta: "Hitta provtagningsställe",
        howEyebrow: "Så fungerar det",
        howTitle: "Du lämnar proverna. Vi håller ihop resten.",
        steps: [
          {
            icon: FileText,
            title: "Vi skickar remissen",
            text: "Vi går igenom ditt underlag, planerar vilka prover som behövs och skickar remissen. Du får instruktioner om vilka prover du ska lämna och när.",
          },
          {
            icon: MapPin,
            title: "Du lämnar prover nära dig",
            text: "Välj ett av de erbjudna provtagningsställena som kan ta just dina prover. Vi hjälper dig att hitta ett lämpligt alternativ nära hemmet eller arbetet.",
          },
          {
            icon: Video,
            title: "Vi sköter uppföljningen online",
            text: "Vi tar emot och granskar dina provsvar och sköter uppföljningen online. Du behöver inte själv hämta resultaten från laboratoriet.",
          },
        ],
        contactCta: "Fråga oss om provtagning nära dig",
      }
    : {
        metaTitle: "Sample collection near you | Intygsläkaren",
        metaDescription:
          "We send the referral. You provide samples at an offered location via Provio, Synlab or Unilabs. Planning and follow-up are handled online.",
        eyebrow: "Sample collection · referrals and online support",
        title: "We send the referral. You provide samples near you.",
        lead: "You do not need to travel to Gothenburg for tests that can be carried out at one of the offered collection locations. We arrange the referral, planning, results and follow-up online.",
        note: "The collection visit is for providing your samples. A separate medical appointment does not need to be booked just to arrange the sampling. If your certificate requires a medical assessment, this is explained on the page for that certificate.",
        primaryCta: "Find your medical certificate",
        secondaryCta: "Find a collection location",
        howEyebrow: "How it works",
        howTitle: "You provide the samples. We take care of the process.",
        steps: [
          {
            icon: FileText,
            title: "We send the referral",
            text: "We review your requirements, plan the tests and send the referral. You receive instructions on which samples to provide and when.",
          },
          {
            icon: MapPin,
            title: "You provide samples near you",
            text: "Choose one of the offered collection locations that can carry out your tests. We help you find a suitable location near your home or work.",
          },
          {
            icon: Video,
            title: "We handle the follow-up online",
            text: "We receive and review your results and manage follow-up online. You do not need to collect the results from the laboratory yourself.",
          },
        ],
        contactCta: "Ask us about sampling near you",
      };

  const startHref = sv ? "/starta-arende" : "/en/start-case";
  const contactHref = sv ? "/kontakt" : "/en/contact";


  return (
    <div className="min-h-screen bg-background">
      <PageMeta title={copy.metaTitle} description={copy.metaDescription} />
      <Header />
      <main>
        <PageHeroCard compact>
          <div className="animate-fade-up max-w-2xl">
            <span className="inline-flex h-7 items-center justify-center rounded-full bg-white/20 px-3.5 text-xs font-semibold uppercase tracking-wider text-white backdrop-blur-sm">
              {copy.eyebrow}
            </span>
            <h1 className="mt-4 text-balance font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-foreground sm:text-5xl lg:text-6xl">
              {copy.title}
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
              {copy.lead}
            </p>
            <p className="mt-3 max-w-xl text-base leading-relaxed text-muted-foreground">
              {copy.note}
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Button asChild size="xl" variant="hero">
                <Link to={startHref}>
                  {copy.primaryCta}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild size="xl" variant="ghost-outline">
                <a href="#provtagningsstallen">{copy.secondaryCta}</a>
              </Button>
            </div>
          </div>
        </PageHeroCard>

        <SamplingLocationsSection className="bg-muted/20" />

        <section className="py-14 lg:py-16">
          <div className="container-wide">
            <div className="mx-auto max-w-3xl text-center">
              <span className="text-sm font-semibold uppercase tracking-wider text-primary">
                {copy.howEyebrow}
              </span>
              <h2 className="mt-2 font-display text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
                {copy.howTitle}
              </h2>
            </div>
            <div className="mt-10 grid gap-6 md:grid-cols-3">
              {copy.steps.map(({ icon: Icon, title, text }) => (
                <article
                  key={title}
                  className="rounded-2xl border border-border/60 bg-card p-6 shadow-card sm:rounded-3xl"
                >
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-primary-soft text-primary">
                    <Icon className="h-5 w-5" aria-hidden />
                  </span>
                  <h3 className="mt-5 font-display text-xl font-semibold text-foreground">{title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="pb-16 lg:pb-20">
          <div className="container-wide">
            <Link
              to={contactHref}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary/80"
            >
              {copy.contactCta}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>

        <Breadcrumbs
          items={[
            { label: sv ? "Hem" : "Home", to: sv ? "/" : "/en" },
            { label: sv ? "Provtagning" : "Sample collection" },
          ]}
        />
      </main>
      <Footer />
    </div>
  );
};

export default Provtagning;
